/**
 * Jira JQL helpers — turn the SPA's project/sprint context into deep links
 * the PM can paste-or-click straight into Jira's issue navigator.
 *
 * Today the host is fixed (Foodics Cloud Jira); when the SPA grows to
 * multi-tenant we'll read this from runtime-config instead.
 */

export const JIRA_BASE_URL = "https://foodics-products.atlassian.net";

const COMPLIANCE_LABEL_PREFIX = "rushd-hygiene-";

/** All compliance rule ids currently shipped by the bot. */
export const COMPLIANCE_RULE_IDS = [
  "missing-sp",
  "missing-end-date",
  "missing-epic",
  "epic-missing-capex",
] as const;
export type ComplianceRuleId = (typeof COMPLIANCE_RULE_IDS)[number];

export function complianceLabel(ruleId: string): string {
  return `${COMPLIANCE_LABEL_PREFIX}${ruleId}`;
}

export function buildJqlUrl(jql: string): string {
  return `${JIRA_BASE_URL}/issues/?jql=${encodeURIComponent(jql)}`;
}

export function buildIssueUrl(key: string): string {
  return `${JIRA_BASE_URL}/browse/${encodeURIComponent(key)}`;
}

/**
 * Deep link to an explicit set of issue keys via `issuekey in (...)`. Unlike
 * the label-based presets, this resolves regardless of whether the hygiene
 * bot has written its `rushd-hygiene-*` labels (compliance tagging may be off
 * for a board) and regardless of project type. Keys are alphanumeric+dash, so
 * no quoting is needed. Returns a project-home link if the list is empty.
 */
export function buildIssueKeysUrl(keys: string[]): string {
  const clean = keys.filter((k) => /^[A-Za-z0-9]+-\d+$/.test(k));
  if (clean.length === 0) return buildJqlUrl("");
  return buildJqlUrl(`issuekey in (${clean.join(", ")})`);
}

/**
 * Link to a Jira project's home. `/browse/<KEY>` with a bare project key (no
 * issue number) redirects to the project's default board/summary for both
 * team- and company-managed projects, so we don't need the project type or a
 * board id — unlike `/jira/software/projects/<KEY>/boards`, which 404s without
 * one (see buildProjectSprintUrl).
 */
export function buildProjectUrl(projectKey: string): string {
  return `${JIRA_BASE_URL}/browse/${encodeURIComponent(projectKey)}`;
}

/**
 * Deep link to the project's latest sprint inside Jira's issue navigator.
 * If the sprint is active we use `openSprints()` so the link auto-rotates
 * as sprints close; otherwise we pin the specific sprint id. Falls back
 * to a project-only filter when no sprint id is known.
 *
 * The plain `/jira/software/projects/<KEY>/boards` URL doesn't redirect
 * to a usable board without an explicit board id, which we don't have
 * on the list endpoint — JQL filters work universally.
 */
export function buildProjectSprintUrl(
  projectKey: string,
  opts: { sprintId?: number | null; isActive?: boolean } = {},
): string {
  const proj = `project = ${jqlQuote(projectKey)}`;
  if (opts.isActive) return buildJqlUrl(`${proj} AND sprint in openSprints()`);
  if (opts.sprintId != null) return buildJqlUrl(`${proj} AND sprint = ${opts.sprintId}`);
  return buildJqlUrl(proj);
}

/**
 * Quote a value for safe use as a JQL string literal. Doubles any
 * embedded double-quote per Jira's escaping rules. Project keys, label
 * names, and rule ids today are alphanumeric+dash so this is mostly
 * defensive — a future custom rule id with quotes won't break the link.
 */
function jqlQuote(v: string): string {
  return `"${v.replace(/"/g, '""')}"`;
}

function labelClause(ruleIds: readonly string[] | string[]): string {
  if (ruleIds.length === 1) return `labels = ${jqlQuote(complianceLabel(ruleIds[0]))}`;
  const list = ruleIds.map((r) => jqlQuote(complianceLabel(r))).join(", ");
  return `labels in (${list})`;
}

const ALL_LABELS_CLAUSE = labelClause(COMPLIANCE_RULE_IDS);

interface ProjectScope {
  projectKey: string;
}
interface SprintScope extends ProjectScope {
  sprintId: number;
  sprintName?: string;
  // Kanban window: there is no real Jira sprint, so membership is
  // "recently updated". When set, presets scope by `updated >= -Nd` instead
  // of `sprint = <synthetic id>` (which Jira would reject).
  windowDays?: number;
}

export interface JqlFilterPreset {
  id: string;
  label: string;
  description: string;
  jql: string;
  url: string;
}

function preset(id: string, label: string, description: string, jql: string): JqlFilterPreset {
  return { id, label, description, jql, url: buildJqlUrl(jql) };
}

/**
 * Project-level presets — the "what's flagged across this whole project" pack.
 * Most useful from the ProjectView page.
 */
export function projectPresets({ projectKey }: ProjectScope): JqlFilterPreset[] {
  const proj = `project = ${jqlQuote(projectKey)}`;
  return [
    preset(
      "all",
      "All hygiene flags",
      "Every ticket the bot is currently flagging in this project, across all sprints.",
      `${proj} AND ${ALL_LABELS_CLAUSE}`,
    ),
    preset(
      "open",
      "Flagged & still open",
      "Only the violations that are still actionable — terminal-status tickets are excluded.",
      `${proj} AND ${ALL_LABELS_CLAUSE} AND statusCategory != Done`,
    ),
    preset(
      "mine",
      "Mine to fix",
      "What you are personally assigned to that the bot has flagged.",
      `${proj} AND assignee = currentUser() AND ${ALL_LABELS_CLAUSE}`,
    ),
    preset(
      "missing-sp",
      "Missing story points",
      "Stories/Tasks/Bugs without an estimate.",
      `${proj} AND ${labelClause(["missing-sp"])}`,
    ),
    preset(
      "missing-end-date",
      "Delivered without End Date",
      "Closed tickets that lack End Date — cycle time can't be measured for these.",
      `${proj} AND ${labelClause(["missing-end-date"])}`,
    ),
    preset(
      "missing-epic",
      "Missing Epic",
      "Stories/Tasks/Bugs not linked to a parent Epic — unrouted work that won't roll up.",
      `${proj} AND ${labelClause(["missing-epic"])}`,
    ),
    preset(
      "epic-missing-capex",
      "Epic missing CapEx flag",
      "Tickets whose parent Epic has no Capatizable value — finance counts these as non-capitalizable until ops classifies the Epic.",
      `${proj} AND ${labelClause(["epic-missing-capex"])}`,
    ),
  ];
}

/**
 * Sprint-level presets — same logic but anchored to a specific sprint.
 * Used inside the SprintView page next to the ComplianceBanner.
 */
export function sprintPresets({ projectKey, sprintId, sprintName, windowDays }: SprintScope): JqlFilterPreset[] {
  const proj = `project = ${jqlQuote(projectKey)}`;
  // Kanban window → `updated >= -Nd`; real sprint → `sprint = <id>`. The
  // synthetic Kanban id is an internal storage key and must never reach Jira.
  const isWindow = windowDays != null;
  const scopeClause = isWindow ? `updated >= -${windowDays}d` : `sprint = ${sprintId}`;
  const unit = isWindow ? "window" : "sprint";
  const niceName = sprintName ? ` (${sprintName})` : "";
  return [
    preset(
      "sprint-all",
      `All flags in this ${unit}${niceName}`,
      `Every still-open flagged ticket in the current ${unit} — terminal-status (Done) tickets excluded.`,
      `${proj} AND ${scopeClause} AND ${ALL_LABELS_CLAUSE} AND statusCategory != Done`,
    ),
    preset(
      "sprint-mine",
      `Mine in this ${unit}`,
      `Your still-open flagged tickets in the current ${unit} — terminal-status (Done) tickets excluded.`,
      `${proj} AND ${scopeClause} AND assignee = currentUser() AND ${ALL_LABELS_CLAUSE} AND statusCategory != Done`,
    ),
    preset(
      "sprint-missing-sp",
      "Missing SP",
      `This ${unit}'s still-open tickets without an estimate — terminal-status (Done) tickets excluded.`,
      `${proj} AND ${scopeClause} AND ${labelClause(["missing-sp"])} AND statusCategory != Done`,
    ),
    preset(
      "sprint-missing-end-date",
      "Delivered without End Date",
      `This ${unit}'s terminal tickets that lack End Date.`,
      `${proj} AND ${scopeClause} AND ${labelClause(["missing-end-date"])}`,
    ),
    preset(
      "sprint-missing-epic",
      "Missing Epic",
      `Every ticket in this ${unit} not linked to a parent Epic, across all statuses.`,
      `${proj} AND ${scopeClause} AND ${labelClause(["missing-epic"])}`,
    ),
    preset(
      "sprint-epic-missing-capex",
      "Epic missing CapEx flag",
      `This ${unit}'s tickets whose parent Epic has no Capatizable value — counted as non-capitalizable until classified. All statuses.`,
      `${proj} AND ${scopeClause} AND ${labelClause(["epic-missing-capex"])}`,
    ),
  ];
}

/** Single-rule deep link used by per-ticket compliance pills. */
export function ruleFilterUrl(projectKey: string, ruleId: string): string {
  const proj = `project = ${jqlQuote(projectKey)}`;
  return buildJqlUrl(`${proj} AND ${labelClause([ruleId])}`);
}
